"use client";

/**
 * Scene 1 — What Survived.
 *
 * Pure dream-first reveal (S53 Strategy A):
 *   SceneHeader + outcome-framed hook + 3 demo cards (3-col)
 *   Micro-proof row + single sticky primary CTA → /app
 *
 * NOTE (S53 Strategy A): Interactive textarea hook removed in favor of clean
 * dopamine reveal + sticky CTA → /app. Reason: foundrystart.com visitors
 * (traffic=0, warm referrals) convert faster on single-click funnel; Notion-
 * pattern hybrid (form at end-of-scrolltell, after warming) deferred to
 * POST-PROD launch (see docs/TODO.md POST-PROD foundations → "Hybrid funnel-
 * entry textarea"). Spec drift: research/landing-v2/09-wireframe-v2-part-a.md
 * §3.7-3.9 sync deferred to S54.
 *
 * Authoritative spec: research/landing-v2/09-wireframe-v2-part-a.md §3 (pre-S53).
 * Concept direction: research/landing-v2/05-landing-concept-v2.md §4.2.
 * Demo data: research/landing-v2/08-demo-niches.md.
 *
 * Reduced-motion:
 *   - Word-stagger hook → single fade-up (handled by SceneHookHero primitive)
 *   - Right-column card entrances → no stagger (handled in NicheCardLanding)
 *   - Top-pick shimmer → static amber ring (handled in FoilCard)
 */

import { motion, useReducedMotion } from "framer-motion";
import { useRouter } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { SceneContainer } from "../shared/SceneContainer";
import { NicheCardLanding } from "../shared/NicheCardLanding";
import { useSceneView } from "../shared/useSceneView";
import { SceneHeader } from "@/components/shared-primitives/SceneHeader";
import { SceneHookHero } from "@/components/shared-primitives/SceneHookHero";
import {
  DEMO_NICHES_SHORT,
  SESSION_META,
  getTopPickShort,
  type DemoNicheShort,
} from "../data/demoNiches.short";

// ─── Copy (single source of truth — scan here for never-say regex) ─────────

/**
 * Scene hook copy — outcome-framed (S53 redesign).
 * Keeps SESSION_META.nichesExplored as a numerical anchor for specificity,
 * but pivots from process language ("died in debate", "past Destroyer") to
 * outcome language ("worth building", "buyer waiting"). Present-tense, alive.
 */
function buildSceneHook(): string {
  const { nichesExplored } = SESSION_META;
  const explored = nichesExplored.toLocaleString("en-US");
  return (
    `${explored} ideas in. Three businesses worth building out. ` +
    `Each with a real buyer already waiting.`
  );
}

const COPY = {
  sceneNumber: 1,
  sceneTitle: "What Survived",
  sceneHook: buildSceneHook(),

  // Micro-proof row — outcome-framed (S53). Audit fineprint removed.
  microProofPrimary: "Three founders. Three businesses worth building.",

  // Card CTA labels — two-tier hierarchy:
  //   Top Pick → demand-tone, possessive ("Show me mine →") — dream-aligned
  //              with brand mission «каждый уникален, у каждого своё место»;
  //              "Steal" implied transferable niche, contradicting that DNA.
  //   Others   → curiosity-pull ("See how →")
  cardCtaTopPick: "Show me mine →",
  cardCtaDefault: "See how →",

  // Founder mirror line — under revenue, single short line (S53).
  cardFounderMirror: "Built by founders like you",

  // Top-pick explainer — micro-text under TOP PICK badge (S53).
  cardTopPickExplainer: "Highest founder-fit score this week.",

  // Primary CTA — end-of-scene (S53 Strategy A).
  ctaLabel: "Get my 3 niches",
  ctaHref: "/app",
  ctaAriaLabel: "Start your own session and get three niches built for you",
} as const;

const APPLE_EASE_OUT = [0.16, 1, 0.3, 1] as const;

// ─── Component ─────────────────────────────────────────────────────────────

export default function Scene1WhatSurvived() {
  const reduced = useReducedMotion();
  const topPickId = getTopPickShort().id;

  useSceneView({
    sceneId: "1",
    sceneName: "What Survived",
    elementId: "scene-1-survived",
  });

  return (
    <SceneContainer
      id="scene-1-survived"
      ariaLabel="What survived last week"
      minHeight="min-h-screen"
    >
      <div className="mx-auto w-full max-w-6xl px-6 py-12 md:py-16 lg:px-8 lg:py-20">
        {/* Header + scene hook */}
        <SceneHeader
          id="scene-1-what-survived-title"
          number={COPY.sceneNumber}
          title={COPY.sceneTitle}
        />

        <SceneHookHero stagger gradient>
          {COPY.sceneHook}
        </SceneHookHero>

        {/* Cards row — full width, dream reveal (S53 redesign).
            md:grid-cols-2 keeps tablet two-up; lg:grid-cols-3 lays all three
            in a single row at desktop ≥1024px (fixes W6 gap 1.6/1.7 orphan). */}
        <div
          role="list"
          aria-label="Sample niches surviving last week"
          className={cn(
            "mt-12 -mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            // Edge fade-mask — visual swipe affordance on mobile (right-edge fade
            // signals "more cards off-screen"). Removed at md+ where grid layout
            // shows all cards simultaneously (no scroll, no need for hint).
            "[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)]",
            "md:mx-0 md:mt-20 md:grid md:snap-none md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 md:pb-0 md:[mask-image:none]",
            "lg:grid-cols-3 lg:gap-7",
          )}
        >
          {DEMO_NICHES_SHORT.map((niche, i) => (
            <div
              key={niche.id}
              role="listitem"
              className="flex min-w-[min(82vw,360px)] snap-center justify-center md:min-w-0"
            >
              <DemoNicheCard
                niche={niche}
                isTopPick={niche.id === topPickId}
                staggerIndex={i}
              />
            </div>
          ))}
        </div>

        {/* Micro-proof row — outcome-framed (S53). */}
        <motion.p
          initial={reduced ? false : { opacity: 0, y: 6 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{
            duration: reduced ? 0 : 0.4,
            delay: reduced ? 0 : 0.2,
            ease: APPLE_EASE_OUT,
          }}
          className={cn(
            "mt-12 text-center text-[13px] uppercase tracking-[0.18em]",
            "font-semibold text-white/55",
          )}
        >
          {COPY.microProofPrimary}
        </motion.p>

        {/* Primary CTA — single sticky conversion anchor (S53 Strategy A). */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 12 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{
            duration: reduced ? 0 : 0.5,
            delay: reduced ? 0 : 0.3,
            ease: APPLE_EASE_OUT,
          }}
          className="mt-12 flex justify-center"
        >
          <a
            href={COPY.ctaHref}
            aria-label={COPY.ctaAriaLabel}
            className={cn(
              "group relative inline-flex items-center gap-2 rounded-full",
              "bg-amber-400 px-7 py-3.5 text-[16px] font-semibold text-bg-base",
              "shadow-[0_8px_32px_rgba(251,191,36,0.35)]",
              "transition-all hover:bg-amber-300",
              "hover:shadow-[0_12px_40px_rgba(251,191,36,0.5)]",
              "hover:-translate-y-0.5 active:translate-y-0",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-amber-300/50",
              "focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base",
            )}
          >
            {COPY.ctaLabel}
            <ArrowRight
              size={18}
              strokeWidth={2.5}
              className="transition-transform group-hover:translate-x-0.5"
              aria-hidden
            />
          </a>
        </motion.div>
      </div>
    </SceneContainer>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

/**
 * Right-column demo card — thin wrapper adapting DemoNiche → NicheCardLanding.
 *
 * Kept co-located (not hoisted to shared/) because the adapter is trivial and
 * nowhere else on the landing maps DemoNiche → card props.
 */
function DemoNicheCard({
  niche,
  isTopPick,
  staggerIndex,
}: {
  niche: DemoNicheShort;
  isTopPick: boolean;
  staggerIndex: number;
}) {
  const router = useRouter();
  const ctaLabel = isTopPick ? COPY.cardCtaTopPick : COPY.cardCtaDefault;
  return (
    <NicheCardLanding
      name={niche.name}
      dreamHook={niche.dreamHook}
      grade={niche.grade}
      heroStatRevenue={niche.heroStat.revenueValue}
      dreamTranslation={niche.dreamTranslation}
      heroStatDay={`day ${niche.heroStat.firstSaleDay}`}
      mood={niche.visualGradient.mood}
      isTopPick={isTopPick}
      ctaLabel={ctaLabel}
      staggerIndex={staggerIndex}
      onClick={() => router.push(`/demo/niche/${niche.id}`)}
      ariaLabel={`Open the full ${niche.name} sample`}
      founderMirror={COPY.cardFounderMirror}
      topPickExplainer={isTopPick ? COPY.cardTopPickExplainer : undefined}
    />
  );
}
